import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Form, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import WidgetPreview from '../components/WidgetPreview';
import { widgetApi } from '../api/api';

const FONTS = ['Inter', 'Roboto', 'Montserrat', 'Poppins', 'Source Code Pro', 'Comic Neue'];
const LAYOUTS = ['horizontal', 'vertical'];
const ANIMATIONS = ['fade', 'slide', 'none'];

export default function WidgetGenerator() {
  const [config, setConfig] = useState(null);
  const [obsUrl, setObsUrl] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    widgetApi.getConfig().then((r) => setConfig(r.data.config)).catch(() => {});
    widgetApi.obsUrl().then((r) => setObsUrl(r.data.obs_url)).catch(() => {});
  }, []);

  const update = useCallback((patch) => {
    setConfig((prev) => ({ ...prev, ...patch }));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await widgetApi.updateConfig(config);
      toast.success('Widget gespeichert!');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(obsUrl);
    toast.success('OBS URL kopiert!');
  };

  if (!config) return <div className="text-muted">Lade…</div>;

  return (
    <div>
      <h4 className="text-white mb-4">🖥️ Widget-Generator</h4>

      <Row className="g-4">
        {/* Config */}
        <Col lg={5}>
          <Card bg="dark" text="white" className="border-secondary">
            <Card.Body>
              <h6 className="text-secondary mb-3">Erscheinungsbild</h6>

              <Form.Group className="mb-3">
                <Form.Label>Schriftart</Form.Label>
                <Form.Select
                  value={config.font}
                  onChange={(e) => update({ font: e.target.value })}
                  className="bg-dark text-white border-secondary"
                >
                  {FONTS.map((f) => <option key={f}>{f}</option>)}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Schriftgröße: {config.font_size}px</Form.Label>
                <Form.Range
                  min={10} max={32} value={config.font_size}
                  onChange={(e) => update({ font_size: +e.target.value })}
                />
              </Form.Group>

              <Row className="mb-3">
                <Col>
                  <Form.Label>Textfarbe</Form.Label>
                  <Form.Control
                    type="color" value={config.text_color}
                    onChange={(e) => update({ text_color: e.target.value })}
                    className="w-100"
                    style={{ height: 40 }}
                  />
                </Col>
                <Col>
                  <Form.Label>Akzentfarbe</Form.Label>
                  <Form.Control
                    type="color" value={config.accent_color || '#1db954'}
                    onChange={(e) => update({ accent_color: e.target.value })}
                    className="w-100"
                    style={{ height: 40 }}
                  />
                </Col>
              </Row>

              <Form.Check
                type="switch" label="Transparenter Hintergrund"
                checked={config.transparent_bg}
                onChange={(e) => update({ transparent_bg: e.target.checked })}
                className="mb-2"
              />
              {!config.transparent_bg && (
                <>
                  <Form.Group className="mb-3">
                    <Form.Label>Hintergrundfarbe</Form.Label>
                    <Form.Control
                      type="color" value={config.bg_color}
                      onChange={(e) => update({ bg_color: e.target.value })}
                      style={{ height: 40 }}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Transparenz: {config.bg_opacity ?? 100}%</Form.Label>
                    <Form.Range
                      min={0} max={100} value={config.bg_opacity ?? 100}
                      onChange={(e) => update({ bg_opacity: +e.target.value })}
                    />
                  </Form.Group>
                </>
              )}

              <h6 className="text-secondary mt-3 mb-2">Layout</h6>
              <Form.Group className="mb-2">
                <Form.Label>Layout</Form.Label>
                <Form.Select
                  value={config.layout}
                  onChange={(e) => update({ layout: e.target.value })}
                  className="bg-dark text-white border-secondary"
                >
                  {LAYOUTS.map((l) => <option key={l}>{l}</option>)}
                </Form.Select>
              </Form.Group>

              <Row className="mb-3">
                <Col>
                  <Form.Label>Breite: {config.width}px</Form.Label>
                  <Form.Range min={200} max={800} value={config.width}
                    onChange={(e) => update({ width: +e.target.value })} />
                </Col>
                <Col>
                  <Form.Label>Höhe: {config.height}px</Form.Label>
                  <Form.Range min={60} max={200} value={config.height}
                    onChange={(e) => update({ height: +e.target.value })} />
                </Col>
              </Row>

              <h6 className="text-secondary mb-2">Optionen</h6>
              <Form.Check type="switch" label="Albumcover anzeigen"
                checked={config.show_cover}
                onChange={(e) => update({ show_cover: e.target.checked })} className="mb-1" />
              <Form.Check type="switch" label="Künstler anzeigen"
                checked={config.show_artist}
                onChange={(e) => update({ show_artist: e.target.checked })} className="mb-1" />
              <Form.Check type="switch" label="Fortschrittsbalken anzeigen"
                checked={config.show_progress}
                onChange={(e) => update({ show_progress: e.target.checked })} className="mb-1" />
              <Form.Check type="switch" label="Titeltext scrollen"
                checked={config.scroll}
                onChange={(e) => update({ scroll: e.target.checked })} className="mb-2" />

              {config.scroll && (
                <Form.Group className="mb-3">
                  <Form.Label>Scroll-Geschwindigkeit: {config.scroll_speed}</Form.Label>
                  <Form.Range min={10} max={100} value={config.scroll_speed}
                    onChange={(e) => update({ scroll_speed: +e.target.value })} />
                </Form.Group>
              )}

              <Form.Group className="mb-3">
                <Form.Label>Animation</Form.Label>
                <Form.Select
                  value={config.animation}
                  onChange={(e) => update({ animation: e.target.value })}
                  className="bg-dark text-white border-secondary"
                >
                  {ANIMATIONS.map((a) => <option key={a}>{a}</option>)}
                </Form.Select>
              </Form.Group>

              <Button variant="success" onClick={save} disabled={saving} className="w-100">
                {saving ? 'Speichere…' : '💾 Speichern'}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Preview + OBS URL */}
        <Col lg={7}>
          <Card bg="dark" text="white" className="border-secondary mb-3">
            <Card.Header>Live-Vorschau</Card.Header>
            <Card.Body className="d-flex justify-content-center py-5"
              style={{ background: '#222', overflowX: 'auto' }}>
              <WidgetPreview config={config} />
            </Card.Body>
          </Card>

          <Card bg="dark" text="white" className="border-secondary">
            <Card.Body>
              <Card.Title>OBS Browser Source URL</Card.Title>
              <code className="text-success d-block mb-2 text-break">{obsUrl}</code>
              <div className="d-flex gap-2">
                <Button variant="success" onClick={copyUrl}>📋 Kopieren</Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => window.open(obsUrl, '_blank')}
                >
                  Vorschau öffnen
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
